import { metric } from '@s4l/monitoring'
import { useS4LContext } from '@s4l/react-context'
import { useEffect, useRef, useCallback } from 'react'
import { ulid } from 'ulid'
import debug from 'debug'

const log = debug('s4l:uikit:SearchComparatorLogger')

export interface LegacyProperty {
  id: number | string
}

export interface SearchComparisonData {
  countsMatch: boolean
  propertyIdsMatch: boolean
  samePropertiesDifferentOrder: boolean
  fullMatch: boolean
  legacyCount: number
  newUseCaseCount: number
  legacyTotalResults: number
  newUseCaseTotalResults: number
  countDifference: number
  missingInNewCount: number
  extraInNewCount: number
  legacyFirstIds: string
  newUseCaseFirstIds: string
  timestamp: string
}

export interface SearchComparatorLoggerProps {
  legacyProperties: LegacyProperty[]
  legacyTotalResults: number
  isLegacyFetching: boolean
  currentPage: number
}

interface ComparisonResult {
  countsMatch: boolean
  propertyIdsMatch: boolean
  orderMatch: boolean
  legacy: {
    count: number
    propertyIds: string[]
  }
  newUseCase: {
    count: number
    propertyIds: string[]
  }
  differences: {
    missingInNew: string[]
    extraInNew: string[]
    samePropertiesDifferentOrder: boolean
  }
}

interface NewSearchResultJSON {
  properties: {
    list: Array<{ id: string; [key: string]: unknown }>
  }
  metadata: {
    count: number
    price: unknown
    [key: string]: unknown
  }
}

function compareResults(
  legacyProperties: LegacyProperty[],
  newSearchResultJSON: NewSearchResultJSON
): ComparisonResult {
  const legacyPropertyIds = legacyProperties.map(p => String(p.id))
  const newPropertiesJSON = newSearchResultJSON.properties
  const newPropertyIds = newPropertiesJSON.list.map(p => String(p.id))

  const countsMatch = legacyProperties.length === newPropertiesJSON.list.length
  const propertyIdsMatch = JSON.stringify(legacyPropertyIds) === JSON.stringify(newPropertyIds)

  const sortedLegacy = [...legacyPropertyIds].sort((a, b) => a.localeCompare(b))
  const sortedNew = [...newPropertyIds].sort((a, b) => a.localeCompare(b))
  const samePropertiesDifferentOrder =
    !propertyIdsMatch && JSON.stringify(sortedLegacy) === JSON.stringify(sortedNew)

  const missingInNew = legacyPropertyIds.filter(id => !newPropertyIds.includes(id))
  const extraInNew = newPropertyIds.filter(id => !legacyPropertyIds.includes(id))

  return {
    countsMatch,
    propertyIdsMatch,
    orderMatch: propertyIdsMatch,
    legacy: {
      count: legacyProperties.length,
      propertyIds: legacyPropertyIds,
    },
    newUseCase: {
      count: newPropertiesJSON.list.length,
      propertyIds: newPropertyIds,
    },
    differences: {
      missingInNew,
      extraInNew,
      samePropertiesDifferentOrder,
    },
  }
}

function buildComparisonData(
  comparison: ComparisonResult,
  legacyTotalResults: number,
  newSearchResultJSON: NewSearchResultJSON
): SearchComparisonData {
  return {
    countsMatch: comparison.countsMatch,
    propertyIdsMatch: comparison.propertyIdsMatch,
    samePropertiesDifferentOrder: comparison.differences.samePropertiesDifferentOrder,
    fullMatch: comparison.countsMatch && comparison.propertyIdsMatch,
    legacyCount: comparison.legacy.count,
    newUseCaseCount: comparison.newUseCase.count,
    legacyTotalResults,
    newUseCaseTotalResults: newSearchResultJSON.metadata.count,
    countDifference: Math.abs(comparison.legacy.count - comparison.newUseCase.count),
    missingInNewCount: comparison.differences.missingInNew.length,
    extraInNewCount: comparison.differences.extraInNew.length,
    legacyFirstIds: comparison.legacy.propertyIds.slice(0, 5).join(','),
    newUseCaseFirstIds: comparison.newUseCase.propertyIds.slice(0, 5).join(','),
    timestamp: new Date().toISOString(),
  }
}

function logComparisonResults(
  comparisonData: SearchComparisonData,
  comparison: ComparisonResult
): void {
  const fullMatchIcon = comparisonData.fullMatch ? '✅' : '❌'
  const countsMatchIcon = comparisonData.countsMatch ? '✅' : '❌'
  const idsMatchIcon = comparisonData.propertyIdsMatch ? '✅' : '❌'
  const orderIcon = comparisonData.samePropertiesDifferentOrder ? '🔀' : '➖'

  log(`
╔══════════════════════════════════════════════════════════════╗
║           🔍 SEARCH COMPARATOR RESULTS                       ║
╠══════════════════════════════════════════════════════════════╣
║  ${fullMatchIcon} Full Match: ${comparisonData.fullMatch ? 'YES' : 'NO'}
║  ${countsMatchIcon} Counts Match: ${comparisonData.countsMatch ? 'YES' : 'NO'}
║  ${idsMatchIcon} Property IDs Match: ${comparisonData.propertyIdsMatch ? 'YES' : 'NO'}
║  ${orderIcon} Same Properties Different Order: ${comparisonData.samePropertiesDifferentOrder ? 'YES' : 'NO'}
╠══════════════════════════════════════════════════════════════╣
║  📊 COUNTS                                                   ║
║  ├─ Legacy Count: ${comparisonData.legacyCount}
║  ├─ New Use Case Count: ${comparisonData.newUseCaseCount}
║  ├─ Legacy Total Results: ${comparisonData.legacyTotalResults}
║  ├─ New Use Case Total Results: ${comparisonData.newUseCaseTotalResults}
║  └─ Difference: ${comparisonData.countDifference === 0 ? '✅ 0' : `⚠️ ${comparisonData.countDifference}`}
╠══════════════════════════════════════════════════════════════╣
║  🏨 PROPERTY IDS (first 5)                                   ║
║  ├─ Legacy: [${comparisonData.legacyFirstIds || 'none'}]
║  └─ New:    [${comparisonData.newUseCaseFirstIds || 'none'}]
╠══════════════════════════════════════════════════════════════╣
║  📋 DIFFERENCES                                              ║
║  ├─ Missing in New: ${comparisonData.missingInNewCount === 0 ? '✅ 0' : `❌ ${comparisonData.missingInNewCount}`}
║  └─ Extra in New: ${comparisonData.extraInNewCount === 0 ? '✅ 0' : `⚠️ ${comparisonData.extraInNewCount}`}
╚══════════════════════════════════════════════════════════════╝
      `)

  if (comparison.differences.missingInNew.length > 0) {
    log(`❌ Missing in New: [${comparison.differences.missingInNew.join(', ')}]`)
  }
  if (comparison.differences.extraInNew.length > 0) {
    log(`⚠️ Extra in New: [${comparison.differences.extraInNew.join(', ')}]`)
  }
}

/**
 * SearchComparatorLogger
 *
 * Temporary component for migration validation. Executes the new DDD use case search
 * when legacy Redux search completes and compares results to ensure parity.
 */
export const SearchComparatorLogger = ({
  legacyProperties,
  legacyTotalResults,
  isLegacyFetching,
  currentPage,
}: SearchComparatorLoggerProps) => {
  const { domain } = useS4LContext()
  const wasLegacyFetching = useRef(isLegacyFetching)

  const processComparison = useCallback(
    (resultJSON: NewSearchResultJSON) => {
      // For pagination, legacy accumulates all properties but new use case returns only current page
      // Slice legacy to compare only the current page's properties
      const PROPERTIES_PER_PAGE = 18
      const startIndex = (currentPage - 1) * PROPERTIES_PER_PAGE
      const currentPageLegacyProperties = legacyProperties.slice(
        startIndex,
        startIndex + PROPERTIES_PER_PAGE
      )

      const comparison = compareResults(currentPageLegacyProperties, resultJSON)
      const comparisonData = buildComparisonData(comparison, legacyTotalResults, resultJSON)

      log(
        'Comparing page %d: legacy properties [%d-%d] vs new use case',
        currentPage,
        startIndex,
        startIndex + currentPageLegacyProperties.length - 1
      )

      logComparisonResults(comparisonData, comparison)

      metric.send({
        id: ulid(),
        name: 'SearchComparatorLogger',
        metadata: {
          match: comparisonData.fullMatch,
        },
      })
    },
    [legacyProperties, legacyTotalResults, currentPage]
  )

  useEffect(() => {
    const executeNewUseCaseSearch = async () => {
      if (wasLegacyFetching.current && !isLegacyFetching) {
        log('Legacy search completed, executing new use case search...')

        try {
          const [error, result] = await domain.SearchFromPartialAndCriteriaPropertyUseCase.execute({
            hasUserInteracted: true,
          })

          if (error || !result) {
            log('Error executing new use case search:', error)
            return
          }

          const resultJSON = result.toJSON() as NewSearchResultJSON
          processComparison(resultJSON)
        } catch (err) {
          log('Exception executing new use case search:', err)
        }
      }

      wasLegacyFetching.current = isLegacyFetching
    }

    executeNewUseCaseSearch()
  }, [isLegacyFetching, domain, processComparison])

  return null
}

SearchComparatorLogger.displayName = 'SearchComparatorLogger'
export default SearchComparatorLogger
